import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  PlusIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const MyEvents = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('registered');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Sample user events data
  const [myEvents] = useState({
    registered: [
      {
        id: 1,
        title: "Independence Day Celebration",
        date: "2024-08-15",
        time: "09:00 AM",
        location: "Apollo Main Campus",
        category: "National Holiday",
        status: "confirmed",
        color: "red",
        attendees: 500,
        registrationDate: "2024-01-15"
      },
      {
        id: 2,
        title: "Advanced Cardiac Surgery Techniques",
        date: "2024-02-15",
        time: "10:00 AM",
        location: "Apollo Medical Center",
        category: "Medical Training",
        status: "confirmed",
        color: "blue",
        attendees: 45,
        registrationDate: "2024-01-20"
      },
      {
        id: 3,
        title: "Digital Health Innovation Summit",
        date: "2024-03-25",
        time: "09:30 AM",
        location: "Convention Center",
        category: "Conference",
        status: "pending",
        color: "purple",
        attendees: 150,
        registrationDate: "2024-01-25"
      }
    ],
    organized: [
      {
        id: 4,
        title: "Monthly Department Meeting - Cardiology",
        date: "2024-02-05",
        time: "02:00 PM",
        location: "Conference Room 1",
        category: "Department Meeting",
        status: "active",
        color: "green",
        attendees: 25,
        createdDate: "2024-01-10"
      },
      {
        id: 5,
        title: "Patient Care Excellence Workshop",
        date: "2024-02-28",
        time: "10:00 AM",
        location: "Training Center",
        category: "Workshop",
        status: "draft",
        color: "orange",
        attendees: 30,
        createdDate: "2024-01-18"
      }
    ],
    past: [
      {
        id: 6,
        title: "New Year Celebration 2024",
        date: "2024-01-01",
        time: "07:00 PM",
        location: "Apollo Main Campus",
        category: "Celebration",
        status: "completed",
        color: "purple",
        attendees: 400,
        registrationDate: "2023-12-20"
      },
      {
        id: 7,
        title: "Christmas Medical Camp",
        date: "2023-12-25",
        time: "09:00 AM",
        location: "Community Center",
        category: "Medical Camp",
        status: "completed",
        color: "green",
        attendees: 200,
        registrationDate: "2023-12-10"
      }
    ]
  });

  const tabs = [
    { id: 'registered', name: 'Registered Events', count: myEvents.registered.length },
    { id: 'organized', name: 'My Organized Events', count: myEvents.organized.length },
    { id: 'past', name: 'Past Events', count: myEvents.past.length }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
    { value: 'draft', label: 'Draft' },
    { value: 'completed', label: 'Completed' }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      red: 'bg-red-50 border-red-200 text-red-800',
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
    };
    return colorMap[color] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const getStatusClasses = (status) => {
    const statusMap = {
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      active: 'bg-blue-100 text-blue-800 border-blue-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      completed: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getCurrentEvents = () => {
    let events = myEvents[activeTab] || [];
    
    // Filter by search term
    events = events.filter(event =>
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter by status
    if (filterStatus !== 'all') {
      events = events.filter(event => event.status === filterStatus);
    }

    return events;
  };

  const filteredEvents = getCurrentEvents();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Component */}
      <Header />

      {/* My Events Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Events</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage your registered and organized events</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-auto pl-8 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent text-sm sm:text-base"
                />
                <FunnelIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-3 top-2.5" />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-apollo-accent focus:border-transparent text-sm sm:text-base"
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <button className="px-3 sm:px-4 py-2 bg-apollo-accent text-white rounded-lg hover:bg-apollo-primary transition-colors font-medium text-sm sm:text-base">
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 inline mr-1 sm:mr-2" />
                Create Event
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto">
            <button 
              onClick={() => navigate('/events')}
              className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap"
            >
              All Events
            </button>
            <button 
              onClick={() => navigate('/events/categories')}
              className="border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 py-4 px-1 text-sm font-medium transition-colors whitespace-nowrap"
            >
              Categories
            </button>
            <button 
              onClick={() => navigate('/events/my-events')}
              className="border-b-2 border-apollo-accent text-apollo-accent py-4 px-1 text-sm font-medium whitespace-nowrap"
            >
              My Events
            </button>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6 sm:mb-8">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-apollo-accent text-apollo-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Events Grid */}
        {filteredEvents.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <CalendarDaysIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">No Events Found</h3>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base px-4">
              {activeTab === 'organized' 
                ? "You haven't organized any events yet. Create your first event to get started."
                : "No events match your current filters. Try adjusting your search or filters."
              }
            </p>
            {activeTab === 'organized' && (
              <button className="px-4 sm:px-6 py-2 sm:py-3 bg-apollo-accent text-white rounded-lg hover:bg-apollo-primary transition-colors font-medium inline-flex items-center space-x-2 text-sm sm:text-base">
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Create Your First Event</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredEvents.map(event => (
              <div key={event.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {event.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getColorClasses(event.color)}`}>
                          {event.category}
                        </span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getStatusClasses(event.status)}`}>
                          {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                        </span>
                      </div>
                    </div>
                    {activeTab === 'organized' && (
                      <div className="flex space-x-1 ml-2">
                        <button className="p-1 text-gray-400 hover:text-apollo-accent transition-colors">
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-apollo-accent transition-colors">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-red-600 transition-colors">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2">
                      <CalendarDaysIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{event.time}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{event.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{event.attendees} attendees</span>
                    </div>
                  </div>

                  <div className="border-t pt-3 sm:pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-xs text-gray-500">
                        {activeTab === 'organized' 
                          ? `Created: ${new Date(event.createdDate).toLocaleDateString()}`
                          : `Registered: ${new Date(event.registrationDate).toLocaleDateString()}`
                        }
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button className="text-apollo-accent hover:text-apollo-primary text-sm font-medium">
                          View Details
                        </button>
                        {activeTab === 'registered' && event.status === 'confirmed' && (
                          <button className="text-red-600 hover:text-red-700 text-sm font-medium">
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {filteredEvents.length > 0 && (
          <div className="mt-8 sm:mt-12 bg-white rounded-lg shadow-sm p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Event Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-apollo-accent">{myEvents.registered.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Registered Events</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-green-600">{myEvents.organized.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Organized Events</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-600">{myEvents.past.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Past Events</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-orange-600">
                  {myEvents.registered.filter(e => e.status === 'confirmed').length}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Confirmed Registrations</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default MyEvents;
